import { useCallback, useEffect, useRef, useState } from "react";
import { type ItemInterface, ReactSortable } from "react-sortablejs";

import { type PlanSet } from "handlers/models/planSet";

import { PlanSetsItem } from "entity/Plans/PlanSets/PlanSetsItem";
import { PlanSetsSkeleton } from "entity/Plans/PlanSets/PlanSetsSkeleton";

import "./PlanSetsPanel.less";

type DraggablePlanSet = PlanSet & ItemInterface;

interface IPlansPanelProps {
    planSets: PlanSet[];
    selectedPlanSetId?: string;
    onSelectPlanSet: (planSet: PlanSet) => void;
    isLoading?: boolean;
    onReorder?: (setIdsInOrder: string[]) => Promise<void>;
    useMobileLayout?: boolean;
}

export function PlanSetsPanel({
    planSets,
    selectedPlanSetId,
    onSelectPlanSet,
    isLoading = false,
    onReorder,
    useMobileLayout = false,
}: Readonly<IPlansPanelProps>) {
    const hasPlanSets = planSets.length > 0;
    const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const panelRef = useRef<HTMLDivElement>(null);

    const [localSets, setLocalSets] = useState<DraggablePlanSet[]>(() =>
        planSets.map((s) => ({ ...s, id: s.setId })),
    );

    useEffect(() => {
        setLocalSets(planSets.map((s) => ({ ...s, id: s.setId })));
    }, [planSets]);

    const handleDragEnd = useCallback(async () => {
        if (!onReorder) return;

        const newOrder = localSets.map((s) => s.setId);
        const currentOrder = planSets.map((s) => s.setId);
        if (newOrder.join(",") === currentOrder.join(",")) return;

        try {
            await onReorder(newOrder);
        } catch {
            // Parent triggers a refetch on failure, which re-syncs localSets
            // through the useEffect above. No additional revert needed here.
        }
    }, [localSets, planSets, onReorder]);

    // Handle keyboard navigation (up/down arrow keys)
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (!hasPlanSets) {
                return;
            }

            const currentIndex = planSets.findIndex((plan) => plan.setId === selectedPlanSetId);

            if (event.key === "ArrowDown") {
                event.preventDefault();
                // Blur any focused button to remove focus outline
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                // Move to next item, or stay at last item
                const nextIndex =
                    currentIndex === -1 ? 0 : Math.min(currentIndex + 1, planSets.length - 1);
                onSelectPlanSet(planSets[nextIndex]);
                // Refocus the panel to maintain keyboard navigation
                panelRef.current?.focus();
            } else if (event.key === "ArrowUp") {
                event.preventDefault();
                // Blur any focused button to remove focus outline
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                // Move to previous item, or stay at first item
                const prevIndex =
                    currentIndex === -1 ? planSets.length - 1 : Math.max(currentIndex - 1, 0);
                onSelectPlanSet(planSets[prevIndex]);
                // Refocus the panel to maintain keyboard navigation
                panelRef.current?.focus();
            }
        },
        [hasPlanSets, planSets, selectedPlanSetId, onSelectPlanSet],
    );

    const planSetsBySetId = new Map(planSets.map((s) => [s.setId, s] as const));
    const isDragEnabled = !!onReorder;

    const renderItems = (sets: { setId: string }[]) =>
        sets
            .filter((entry) => planSetsBySetId.has(entry.setId))
            .map((entry) => {
                const plan = planSetsBySetId.get(entry.setId)!;
                return (
                    <PlanSetsItem
                        key={plan.setId}
                        ref={(el) => {
                            if (el) {
                                itemRefs.current.set(plan.setId, el);
                            } else {
                                itemRefs.current.delete(plan.setId);
                            }
                        }}
                        planSet={plan}
                        isSelected={plan.setId === selectedPlanSetId}
                        onClick={onSelectPlanSet}
                        isDragEnabled={isDragEnabled}
                        useMobileLayout={useMobileLayout}
                    />
                );
            });

    return (
        <div
            ref={panelRef}
            className="plans-sets-panel"
            data-testid="plans-sets-panel"
            onKeyDown={handleKeyDown}
            role="list"
            aria-label="Plans sets panel"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
        >
            {isLoading ? (
                <div className="plans-sets-list-container" data-testid="plans-sets-list">
                    <PlanSetsSkeleton count={3} />
                </div>
            ) : onReorder ? (
                <div className="plans-sets-list-container" data-testid="plans-sets-list">
                    <ReactSortable
                        list={localSets}
                        setList={setLocalSets}
                        onEnd={handleDragEnd}
                        handle={useMobileLayout ? undefined : ".DragAndDropHandle"}
                        delay={useMobileLayout ? 500 : 0}
                        delayOnTouchOnly={useMobileLayout}
                        touchStartThreshold={5}
                        animation={200}
                        fallbackOnBody
                    >
                        {renderItems(localSets)}
                    </ReactSortable>
                </div>
            ) : (
                <div className="plans-sets-list-container" data-testid="plans-sets-list">
                    {renderItems(planSets)}
                </div>
            )}
        </div>
    );
}
